import { Checkbox, Col, Row, Select, Space, Input } from 'antd'
import {
  V3TransactionStartMethod,
  V3TransactionState,
} from '../../../../models/v3-transaction'
import { TenantRef } from '../../../../models/tenant'
import { V3TransactionFilter } from '../../../../services/data-provider/v3-transaction'
import { theme } from '../../../../theme'
import { AdvancedSearchWrapper } from '../../../../atom/collapse'
import { useAppState } from '../../../../state'
import UsernameSelector from '../../../transactions/components/advancedSearch/UsernameSelector'
import ResetButton from '../../../transactions/components/advancedSearch/ResetButton'
import SearchButton from '../../../transactions/components/advancedSearch/SearchButton'
import { ButtonIcon } from '../../../../atom/button'
import { magnifyBlackSVG, magnifySVG } from '../../../../assets/svg/magnify'
import { ReusableSelector } from 'components/reusableSelector/selector'
import { ReusableSelectorInputWrapper } from '../../../../atom/select'
import { useChargerTranslation } from 'hooks/translation/useChargerTranslation'
import { ChargerRef } from 'models/charger'
import { useSelector } from 'hooks/useSelector'
import { ReactElement } from 'react'
import { useTransactionSearchTranslation } from 'hooks/translation/useTransactionSearchTranslation'
import { DropdownWrapper } from 'atom/user-edit'
import { formatStartMethodDisplay } from 'helpers/formatStartMethodDisplay'
import { renderFormatMessage } from 'helpers/intl'

const transactionStartMethodsArr: V3TransactionStartMethod[] = ['mobile', 'web', 'rfid', 'plug_and_charge', 'plug_and_play', 'pos', 'sms', 'partner', 'ocpi']
const transactionStateV3Arr: V3TransactionState[] = ['initiating', 'failed_initiation', 'verified', 'failed_charging', 'starting', 'in_progress', 'pending_unplug', 'completed', 'hardware_error']

export const paymentStateArr: string[] = [
  'initiating',
  'calculating',
  'calculating_failed',
  'data_validating',
  'pending_assessment',
  'voided',
  'settlement_initiated',
  'settlement_pending',
  'settlement_outstanding',
  'invoiced',
  'settlement_failed',
  'settlement_pass',
  'settled',
]

export interface AdvancedSearchProps {
  loading: boolean
  filter: V3TransactionFilter
  tenants: TenantRef[]
  defaultTenant?: TenantRef
  showAdvancedSearch: boolean
  disabled: boolean
  reset: boolean
  onState: (state: V3TransactionState) => void
  onPaymentState: (paymentState: string | undefined) => void
  onUsername: (user: string) => void
  onChargerId: (chargerId: string[]) => void
  onStartMethod: (value: V3TransactionStartMethod) => void
  onFetchTransactions: () => void
  onResetAdvancedSearch: () => void
  onFetchDataWithQuery: () => void
  onOcppTransactionId: (value: string) => void
}

const advancedSearchStyle = {
  border: `1px solid ${theme.colors.lightGray}`,
  borderRadius: '4px',
  padding: '20px',
  backgroundColor: theme.colors.white,
}


const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  loading,
  tenants,
  defaultTenant,
  filter,
  showAdvancedSearch,
  disabled,
  reset,
  onStartMethod,
  onChargerId,
  onFetchTransactions,
  onResetAdvancedSearch,
  onState,
  onPaymentState,
  onUsername,
  onFetchDataWithQuery,
  onOcppTransactionId,
}) => {
  const { IsDesktop, IsLaptop, isMockUpEnabled, currentUser } = useAppState()
  const { serialNumbersText } = useChargerTranslation()
  const { startMethodText, tranasctionStateText, paymentStateText } = useTransactionSearchTranslation()
  const {
    multiSerialNumberSelectorDebounceFetcher,
    multiSerialNumberHandleChange,
    startMethodHandleValue,
    stateHandleValue,
    multiSerialNumberHandleValue,
    multiSerialNumberHandlePlaceholder,
    startMethodHandleChange,
    stateHandleChange,
  } = useSelector()

  const isDesktop = IsDesktop()
  const isLaptop = IsLaptop()

  const renderChargerSerialNumberOption = (
    charger: ChargerRef,
    selectedOptions: any[],
    handleCheckboxClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  ): ReactElement => (
    <Select.Option
      key={charger.chargePointSerialNumber}
      value={charger.chargePointSerialNumber}
      label={charger.chargePointSerialNumber}
    >
      <Checkbox
        onClick={handleCheckboxClick}
        checked={selectedOptions.findIndex((option) => option.id === charger.id) > -1}
      >
        {charger.chargePointSerialNumber}
      </Checkbox>
    </Select.Option>
  )

  const paymentStateDisplayNames: Record<string, string> = {
    invoiced: 'Invoiced',
  }

  const formatV3StateName = (state: string): string => {
    return state
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const formatPaymentStateDisplay = (state: string): string =>
    paymentStateDisplayNames[state] || formatV3StateName(state)

  const paymentStateHandleValue = (f: V3TransactionFilter | undefined) => f?.paymentState

  const renderPaymentStateOption = (
    option: any,
    selectedOptions: any,
    handleCheckboxClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void,
    filter?: V3TransactionFilter,
  ): ReactElement => {
    const formatted = formatPaymentStateDisplay(option.state)
    return (
      <Select.Option key={`${option.state}-${option.index}`} value={option.state} label={option.state}>
        <DropdownWrapper selected={filter?.paymentState === option.state}>{formatted}</DropdownWrapper>
      </Select.Option>
    )
  }

  const renderStateOption = (
    option: any,
    selectedOptions: any,
    handleCheckboxClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void,
    filter?: V3TransactionFilter,
  ): ReactElement => {
    const formattedState = formatV3StateName(option.state)
    return (
      <Select.Option key={`${option.state}-${option.index}`} value={option.state} label={option.state}>
        <DropdownWrapper selected={filter?.state === option.state}>
          {renderFormatMessage(`dashboard.transactionsPage.advancedSearch.state.${option.state}`, formattedState)}
        </DropdownWrapper>
      </Select.Option>
    )
  }

  const renderStartMethodOption = (
    option: any,
    selectedOptions: any,
    handleCheckboxClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void,
    filter?: V3TransactionFilter,
  ): ReactElement => {
    const formattedStartMethod = formatStartMethodDisplay(option.state)
    return (
      <Select.Option key={`${option.state}-${option.index}`} value={option.state} label={option.state}>
        <DropdownWrapper selected={filter?.startMethod === option.state}>
          {renderFormatMessage(
            `dashboard.transactionsPage.advancedSearch.startMethod.${option.state.toLowerCase()}`,
            formattedStartMethod,
          )}
        </DropdownWrapper>
      </Select.Option>
    )
  }

  const ocppFilterValue = (filter as any).ocppTransactionId || ''

  return (
    <>
      {isDesktop || isLaptop ? (
        <AdvancedSearchWrapper showAdvancedSearch={showAdvancedSearch}>
          <Row style={advancedSearchStyle}>
            <Space wrap size={[9, 9]}>
              <ReusableSelector
                showSearch={false}
                filter={filter as any}
                onClear={() => {}}
                customWidth="290px"
                isSingle={true}
                placeholder={startMethodText}
                dropDownList={transactionStartMethodsArr}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={false}
                noSearch={true}
                debounceFetcher={undefined}
                customHeight="37px"
                reset={reset}
                onOptionsChange={onStartMethod}
                handleOptionChange={startMethodHandleChange}
                renderOption={renderStartMethodOption}
                handleValue={startMethodHandleValue}
                isMockUpEnabled={isMockUpEnabled}
              />
              <ReusableSelector
                showSearch={false}
                filter={filter as any}
                onClear={() => {}}
                isSingle={true}
                placeholder={tranasctionStateText}
                dropDownList={transactionStateV3Arr}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={false}
                noSearch={true}
                debounceFetcher={undefined}
                customHeight="37px"
                customWidth="290px"
                reset={reset}
                onOptionsChange={onState}
                handleOptionChange={stateHandleChange}
                renderOption={renderStateOption}
                handleValue={stateHandleValue}
                isMockUpEnabled={isMockUpEnabled}
              />
              <ReusableSelector
                showSearch={false}
                filter={filter as any}
                onClear={() => {}}
                isSingle={true}
                placeholder={paymentStateText}
                dropDownList={paymentStateArr}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={false}
                noSearch={true}
                debounceFetcher={undefined}
                customHeight="37px"
                customWidth="290px"
                reset={reset}
                onOptionsChange={onPaymentState}
                handleOptionChange={stateHandleChange}
                renderOption={renderPaymentStateOption}
                handleValue={paymentStateHandleValue}
                isMockUpEnabled={isMockUpEnabled}
              />
              <ReusableSelector
                showSearch
                onOptionsChange={(chargerId: ChargerRef[]) =>
                  onChargerId(chargerId.map((charger) => charger.chargePointSerialNumber))
                }
                dropdownClassName="not-in-modal-selector"
                filter={filter as any}
                onClear={() => {}}
                customWidth="290px"
                isSingle={false}
                placeholder={serialNumbersText}
                dropDownList={tenants}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={true}
                debounceFetcher={multiSerialNumberSelectorDebounceFetcher}
                customHeight="37px"
                reset={reset}
                showArrow={false}
                handleOptionChange={multiSerialNumberHandleChange}
                renderOption={renderChargerSerialNumberOption}
                handleValue={multiSerialNumberHandleValue}
                handlePlaceholder={multiSerialNumberHandlePlaceholder}
                isMockUpEnabled={isMockUpEnabled}
              />
              {currentUser?.role === 'admin' && (
                <ReusableSelectorInputWrapper customWidth="290px" customHeight="37px">
                  {magnifySVG}
                  <Input
                    placeholder="OCPP Transaction ID(s)"
                    value={ocppFilterValue}
                    onChange={(e) => onOcppTransactionId(e.target.value)}
                    onPressEnter={onFetchDataWithQuery}
                    allowClear
                  />
                </ReusableSelectorInputWrapper>
              )}
              <UsernameSelector onUsername={onUsername} filter={filter as any} onFetchTransactions={onFetchDataWithQuery} />
              <ButtonIcon
                icon={magnifyBlackSVG}
                fetchData={onFetchDataWithQuery}
                loading={loading}
                disabled={disabled}
              />
              <div className="reset-button">
                <ResetButton onResetAdvancedSearch={onResetAdvancedSearch} />
              </div>
            </Space>
          </Row>
        </AdvancedSearchWrapper>
      ) : (
        <AdvancedSearchWrapper showAdvancedSearch={showAdvancedSearch}>
          <Row gutter={[8, 8]} style={advancedSearchStyle}>
            <Col xs={24} md={12}>
              <ReusableSelector
                showSearch={false}
                filter={filter as any}
                onClear={() => {}}
                customWidth="341px"
                isSingle={true}
                placeholder={startMethodText}
                dropDownList={transactionStartMethodsArr}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={false}
                noSearch={true}
                debounceFetcher={undefined}
                customHeight="37px"
                reset={reset}
                onOptionsChange={onStartMethod}
                handleOptionChange={startMethodHandleChange}
                renderOption={renderStartMethodOption}
                handleValue={startMethodHandleValue}
                isMockUpEnabled={isMockUpEnabled}
              />
            </Col>
            <Col xs={24} md={12}>
              <ReusableSelector
                showSearch={false}
                filter={filter as any}
                onClear={() => {}}
                customWidth="341px"
                isSingle={true}
                placeholder={tranasctionStateText}
                dropDownList={transactionStateV3Arr}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={false}
                noSearch={true}
                debounceFetcher={undefined}
                customHeight="37px"
                reset={reset}
                onOptionsChange={onState}
                handleOptionChange={stateHandleChange}
                renderOption={renderStateOption}
                handleValue={stateHandleValue}
                isMockUpEnabled={isMockUpEnabled}
              />
            </Col>
            <Col xs={24} md={12}>
              <ReusableSelector
                showSearch={false}
                filter={filter as any}
                onClear={() => {}}
                customWidth="341px"
                isSingle={true}
                placeholder={paymentStateText}
                dropDownList={paymentStateArr}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={false}
                noSearch={true}
                debounceFetcher={undefined}
                customHeight="37px"
                reset={reset}
                onOptionsChange={onPaymentState}
                handleOptionChange={stateHandleChange}
                renderOption={renderPaymentStateOption}
                handleValue={paymentStateHandleValue}
                isMockUpEnabled={isMockUpEnabled}
              />
            </Col>
            <Col xs={24} md={12}>
              <ReusableSelector
                showSearch
                onOptionsChange={(chargerId: ChargerRef[]) =>
                  onChargerId(chargerId.map((charger) => charger.chargePointSerialNumber))
                }
                dropdownClassName="not-in-modal-selector"
                filter={filter as any}
                onClear={() => {}}
                customWidth="341px"
                isSingle={false}
                placeholder={serialNumbersText}
                dropDownList={tenants}
                showMagnifySVG={true}
                maxTagCount={0}
                isDebounceFetcher={true}
                debounceFetcher={multiSerialNumberSelectorDebounceFetcher}
                customHeight="37px"
                reset={reset}
                handleOptionChange={multiSerialNumberHandleChange}
                renderOption={renderChargerSerialNumberOption}
                handleValue={multiSerialNumberHandleValue}
                handlePlaceholder={multiSerialNumberHandlePlaceholder}
                isMockUpEnabled={isMockUpEnabled}
              />
            </Col>
            {currentUser?.role === 'admin' && (
              <Col xs={24} md={12}>
                <ReusableSelectorInputWrapper customWidth="341px" customHeight="37px">
                  {magnifySVG}
                  <Input
                    placeholder="OCPP Transaction ID(s)"
                    value={ocppFilterValue}
                    onChange={(e) => onOcppTransactionId(e.target.value)}
                    onPressEnter={onFetchDataWithQuery}
                    allowClear
                  />
                </ReusableSelectorInputWrapper>
              </Col>
            )}
            <Col xs={24} md={12}>
              <UsernameSelector onUsername={onUsername} filter={filter as any} onFetchTransactions={onFetchDataWithQuery} />
            </Col>
            <Col span={12}>
              <SearchButton onFetchTransactions={onFetchDataWithQuery} loading={loading} disabled={disabled} />
            </Col>
            <Col span={12} className="reset-button">
              <ResetButton onResetAdvancedSearch={onResetAdvancedSearch} />
            </Col>
          </Row>
        </AdvancedSearchWrapper>
      )}
    </>
  )
}

export default AdvancedSearch

